<template>
  <div class="box">
    <div class="headline">
      <div class="top">Step 4 of 5</div>
      {{ title }}
    </div>
    <b-form>
      <b-form-group
        md-label-cols="2"
        label="Name"
        description="Field name, lowercase"
        class="required"
        :invalidFeedback="errorName"
        :state="stateName"
      >
        <b-form-input v-model="field.name" placeholder="example: isbn" required :state="stateName"></b-form-input>
      </b-form-group>
      <b-form-group
        label-cols="2"
        label="Type"
        description="Field type"
        class="required"
        :invalidFeedback="errorType"
        :state="stateType">
        <b-form-select v-model="field.type" :options="FIELD_TYPE_OPTIONS" :state="stateType"></b-form-select>
      </b-form-group>
      <b-form-group
        v-if="isRelational"
        label-cols="2"
        label="To"
        description="Class to which this model is related"
        class="required"
        :invalidFeedback="errorTarget"
        :state="stateTarget"
      >
        <b-form-select v-model="field.target" :options="relationTargets" required :state="stateTarget"></b-form-select>
      </b-form-group>
      <b-form-group
        v-if="isSingleRelational"
        label-cols="2"
        label="On delete"
        description="What to do with the instance if related model is deleted"
        class="required"
        :invalidFeedback="errorOnDelete"
        :state="stateOnDelete"
      >
        <b-form-select v-model="field.on_delete" :options="ON_DELETE_OPTIONS" required :state="stateOnDelete"></b-form-select>
      </b-form-group>
      <b-form-group
        v-if="field.type === 'models.CharField'"
        label-cols="2"
        label="Choices"
        description="Require value to be from a list of possible choices"
        :invalidFeedback="errorChoices"
        :state="stateChoices"
      >
        <b-form-checkbox v-model="field.useChoices" :state="stateChoices">
          Enable
          <a v-if="field.useChoices" href="#" class="choices-link" @click.prevent="$refs.fieldChoices.show">edit choices ...</a>
        </b-form-checkbox>
      </b-form-group>
      <b-form-group label-cols="2" label="Default" description="Default value (if set, field will not be required)" v-if="canHaveDefault">
        <b-form-select v-if="field.type === 'models.BooleanField'" v-model="field.default" :options="BOOL_DEFAULT_OPTIONS"></b-form-select>
        <b-form-select v-else-if="isDate" v-model="field.default" :options="field.type === 'models.DateTime' ? DATETIME_DEFAULT_OPTIONS : DATE_DEFAULT_OPTIONS"></b-form-select>
        <b-form-select v-else-if="field.useChoices" v-model="field.default" :options="field.choices"></b-form-select>
        <b-form-input v-else v-model="field.default" placeholder="example: 1234"></b-form-input>
      </b-form-group>
      <b-form-group v-if="isLimitedText" label-cols="2" label="Max length" description="Maximum length of the value of the field">
        <b-form-input v-model="field.max_len" type="number" placeholder="Example: 255"></b-form-input>
      </b-form-group>

      <b-row>
        <b-col cols="12" sm="6">
          <b-form-group v-if="isNumber" label="Min value" description="Minimum value of the field">
            <b-form-input v-model="field.min_val"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group v-if="isNumber" label="Max value" description="Maximum value of the field">
            <b-form-input v-model="field.max_val"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="field.type === 'models.DecimalField'">
        <b-col cols="12" sm="6">
          <b-form-group
            label="Max digits"
            description="Maximum number of digits (including decimal places)"
            class="required"
            :invalidFeedback="errorMaxDigits"
            :state="stateMaxDigits"
          >
            <b-form-input v-model="field.max_digits" type="number" :state="stateMaxDigits"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group
            label="Decimal places"
            description="Precision (number of decimal places)"
            class="required"
            :invalidFeedback="errorDecimalPlaces"
            :state="stateDecimalPlaces"
          >
            <b-form-input v-model="field.decimal_places" type="number" :state="stateDecimalPlaces"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" sm="6">
          <b-form-group description="Whether the value should be unique">
            <b-form-checkbox v-model="field.unique" :disabled="field.type === 'models.OneToOneField' || isM2M">Unique</b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group :description="'Whether the field can be ' + ((isText || isM2M) ? 'blank' : 'NULL') + ' (if set, field will not be required)'">
            <b-form-checkbox v-model="field.null" :disabled="field.on_delete === 'SET_NULL'">
              Allow {{(isText || isM2M) ? 'blank' : 'NULL'}}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label-cols="2" label="Verbose name" description="Human-readable field name">
        <b-form-input v-model="field.verbose_name" placeholder="example: ISBN"></b-form-input>
      </b-form-group>
      <b-form-group label-cols="2" label="Help text" description="One-line description/help for the field shown in Django admin">
        <b-form-input v-model="field.help_text" placeholder="example: Book's international standard book number"></b-form-input>
      </b-form-group>

      <b-row>
        <b-col cols="12" sm="6">
          <b-form-group description="Include in serialized responses / deserialized requests">
            <b-form-checkbox v-model="field.includeInSerializer">Serialize</b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group description="If set, you'll need to manually set the value of this field in the code later">
            <b-form-checkbox v-model="field.read_only">Read-only</b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="field.includeInSerializer">
        <b-col cols="12" sm="6" v-if="canHaveViewSearch">
          <b-form-group
            description="Enable filtering GET results using case-insensitive (sub)string match on the contents of this field (field__icontains=<query>)."
          >
            <b-form-checkbox v-model="field.apiSearch">Case-insensitive substring filtering in API</b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" v-if="canHaveViewFilter">
          <b-form-group
            description="Enable filtering GET results using exact comparison with the contents of this field. (field__exact=<query>)"
          >
            <b-form-checkbox v-model="field.apiFilter">Exact lookup filtering in API</b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" sm="6">
          <b-form-group v-if="useAdmin" description="Show as column in list of objects in Django admin interface">
            <b-form-checkbox v-model="field.adminListField" :disabled="isM2M">Admin List</b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6">
          <b-form-group v-if="useAdmin" description="Use as search criteria in Django admin interface">
            <b-form-checkbox v-model="field.adminSearchBy" :disabled="isM2M">Searchable in admin</b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

    </b-form>
    <div class="button-row">
      <b-button @click="$emit('cancel')">Cancel</b-button>
      <b-button v-if="!isNew" variant="danger" v-b-modal.confirmRemove>Remove field</b-button>
      <b-button variant="primary" @click="apply">{{ isNew ? 'Save new field' : 'Save changes' }}</b-button>
    </div>
    <FieldChoices :field="field" ref="fieldChoices" @choices="field.choices = $event" />

    <b-modal id="confirmRemove" title="Remove Field" @ok="$emit('remove')" ok-title="Remove" ok-variant="danger">
      <p>
        Really remove field <code>{{ field.name }}</code> from {{ model.name }}?
      </p>
    </b-modal>

  </div>
</template>

<script>
import {
  EMPTY_FIELD,
  FIELD_TYPE_OPTIONS,
  RELATIONAL_FIELDS,
  SINGLE_REL_FIELDS,
  TEXT_FIELDS,
  LIMITED_TEXT_FIELDS,
  NUMBER_FIELDS,
  FILE_FIELDS,
  ON_DELETE_OPTIONS,
  DATE_FIELDS,
  DATETIME_DEFAULT_OPTIONS,
  DATE_DEFAULT_OPTIONS,
  BOOL_DEFAULT_OPTIONS,
  PYTHON_KEYWORDS
} from '../../helpers/constants'

import FieldChoices from './FieldChoices'
import { mapErrorStates } from '../../helpers/errors'
import {toValidAttributeName} from '../../helpers/python'

export default {
  name: 'Field',

  components: {
    FieldChoices
  },

  props: {
    recipe: {type: Object, required: true},
    model: {type: Object, required: true},
    useAdmin: {type: Boolean, required: true},
    instance: {type: Object, required: false}
  },

  data () {
    const isNew = !this.instance
    const field = isNew ? {...EMPTY_FIELD} : JSON.parse(JSON.stringify(this.instance))
    if (isNew) field.name = 'field' + (this.model.fields.length + 1)

    if (!isNew) {
      const model = this.model
      const name = field.name
      const admin = model.admin

      if (admin) {
        field.adminListField = !!(admin.list_fields && admin.list_fields.includes(name))
        field.adminSearchBy = !!(admin.search_fields && admin.search_fields.includes(name))
      }

      if (model.viewset && model.viewset.filter) {
        const filter = model.viewset.filter
        console.log(filter.search_fields, name, filter.search_fields.includes(name))
        field.apiFilter = !!(filter.filter_fields && filter.filter_fields.includes(name))
        field.apiSearch = !!(filter.search_fields && filter.search_fields.includes(name))
      }

      field.includeInSerializer = !!(
        model.serializer && model.serializer.fields && model.serializer.fields.includes(name)
      )
    }

    return {
      isNew,
      field,
      choices: {},
      errors: {},

      FIELD_TYPE_OPTIONS,
      ON_DELETE_OPTIONS,
      BOOL_DEFAULT_OPTIONS,
      DATETIME_DEFAULT_OPTIONS,
      DATE_DEFAULT_OPTIONS
    }
  },

  computed: {
    title () {
      return this.isNew ? `Add new field to ${this.model.name}` : `Edit field ${this.model.name}.${this.field.name}`
    },

    relationTargets () {
      let list = []

      if (this.recipe.auth) list.push('users.User')

      for (let app of this.recipe.apps) {
        for (let model of app.models) {
          if (!model.name) continue
          list.push(`${app.name}.${model.name}`)
        }
      }
      return list
    },

    isRelational () {
      return RELATIONAL_FIELDS.includes(this.field.type)
    },

    isSingleRelational () {
      return SINGLE_REL_FIELDS.includes(this.field.type)
    },

    isText () {
      return TEXT_FIELDS.includes(this.field.type)
    },

    isLimitedText () {
      return LIMITED_TEXT_FIELDS.includes(this.field.type)
    },

    isNumber () {
      return NUMBER_FIELDS.includes(this.field.type)
    },

    isFile () {
      return FILE_FIELDS.includes(this.field.type)
    },

    isDate () {
      return DATE_FIELDS.includes(this.field.type)
    },

    isM2M () {
      return this.field.type === 'models.ManyToManyField'
    },

    canHaveDefault () {
      return (!this.isRelational && !this.isFile && this.field.type !== 'JSONField')
    },

    canHaveViewSearch () {
      return this.field.includeInSerializer && this.isText
    },

    canHaveViewFilter () {
      return this.field.includeInSerializer && this.isSingleRelational || this.field.useChoices
    },

    ...mapErrorStates(['name', 'type', 'target', 'on_delete', 'max_len', 'max_digits', 'decimal_places', 'choices'])
  },

  watch: {
    'field.name': function () {
      this.field.name = toValidAttributeName(this.field.name)
    },

    'field.on_delete': function () {
      if (!this.isSingleRelational) return

      if (this.field.on_delete === 'SET_NULL') {
        this.field.null = true
      }
    },

    'field.type': function () {
      if (this.field.type === 'models.OneToOneField') {
        this.field.unique = true
      }

      if (this.isM2M) {
        this.field.adminListField = false
        this.field.adminSearchBy = false
      }

      if (!this.canHaveViewFilter) this.field.apiFilter = false
      if (!this.canHaveViewSearch) this.field.apiSearch = false
    }
  },

  methods: {
    validate: function () {
      const f = this.field

      this.errors = {}

      if (!f.name) this.$set(this.errors, 'name', 'Field name must be a valid Python attribute name')
      if (f.name && f.name.match(/[A-Z]/)) this.$set(this.errors, 'name', 'Field name should use underscore_case instead of camelCase')
      if (f.name === 'id') this.$set(this.errors, 'name', 'Field name must not clash with the automatic primary key ("id")')
      if (PYTHON_KEYWORDS.includes(f.name)) this.$set(this.errors, 'name', 'Field name must not be a reserved Python keyword')
      if (f.name === 'objects') this.$set(this.errors, 'name', 'Field name must not clash with the default model manager ("objects")')
      if (!f.type) this.$set(this.errors, 'type', 'A valid field type is required')
      if (this.isRelational && !f.target) this.$set(this.errors, 'target', 'Relational field requires a target model')
      if (this.isSingleRelational && !f.on_delete) this.$set(this.errors, 'on_delete', 'ON_DELETE behaviour must be specified')
      if (f.useChoices && Object.keys(f.choices).length === 0) this.$set(this.errors, 'choices', 'At least one choice must be provided')
      if (f.type === 'models.DecimalField') {
        if (!f.max_digits) this.$set(this.errors, 'max_digits', 'Max. digits limit must be set')
        if (!f.decimal_places) this.$set(this.errors, 'decimal_places', 'Number of decimal places must be set')
        f.max_digits = parseInt(f.max_digits)
        f.decimal_places = parseInt(f.decimal_places)
        if (f.decimal_places > f.max_digits) this.$set(this.errors, 'decimal_places', 'Number of decimal places can\'t be larger than max digits')
      }

      if (!this.isSingleRelational) {
        f.on_delete = null
      }

      for (let f2 of this.model.fields) {
        if (
          (f2 !== this.instance) &&
          (f2.name === f.name)
        ) {
          this.$set(this.errors, 'name', `Field with this name is already defined on ${this.model.name}`)
        }
      }
      return Object.keys(this.errors).length === 0
    },

    apply () {
      if (!this.validate()) return

      const f = this.field
      if (f.type === 'models.BooleanField') {
        switch (f.default) {
          case 'null':
            f.default = null
            break
          case 'true':
            f.default = true
            break
          case 'false':
            f.default = false
            break
        }
      }

      if (this.isDate && f.default === 'null') f.default = null

      this.$emit(this.isNew ? 'create' : 'update', this.field)
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/wizard/Field.vue